import { useEffect, useState, useMemo } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api } from '../services/api'
import { useToast, ConfirmModal, SearchIcon, FilterIcon, PlusIcon, TrashIcon } from '../components/ui'
import { SkeletonTable } from '../components/ui/Skeleton'
import MemberDetailDrawer from './MemberDetailDrawer'
import { isTenantInstance } from '../lib/runtimeConfig'
import { createUserErrorMessage, isInvitationSent } from '../utils/memberInvite'
import { displayIdentity } from '../utils/identityDisplay'
import './Members.css'

interface Organization {
  id: number
  name: string
  slug: string
    public_token?: string
  member_role: string
}

type AccountType = 'person' | 'service' | 'mailbox'

interface Member {
  id: number
  user_id: number
  email: string
  first_name?: string
  last_name?: string
  role: string
  status: string
  joined_at?: string
  /** Aus Keycloak-User-Attribute `account_type`. Default 'person'. */
  account_type?: AccountType
  /** Keycloak-User-UUID — primaerer Member-Ref fuer Detail-Drawer/API. */
  keycloak_id?: string
}

interface OrgUser {
  keycloak_id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  status: string
  enabled: boolean | null
}

interface Props {
  organization: Organization
}

type RoleFilter = 'all' | 'owner' | 'admin' | 'member' | 'operator' | 'viewer'
type StatusFilter = 'all' | 'active' | 'pending' | 'removed'
type ViewMode = 'members' | 'users'
type AccountTypeFilter = 'all' | AccountType

const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  person: 'Person',
  service: 'Service',
  mailbox: 'Postfach',
}

const ACCOUNT_TYPE_ICON: Record<AccountType, string> = {
  person: '👤',
  service: '🤖',
  mailbox: '📬',
}

const ACCOUNT_TYPE_COLORS: Record<AccountType, { bg: string; fg: string }> = {
  person: { bg: '#dbeafe', fg: '#1e40af' },
  service: { bg: '#ede9fe', fg: '#6d28d9' },
  mailbox: { bg: '#fef3c7', fg: '#92400e' },
}

export default function Members({ organization }: Props) {
  const { showToast } = useToast()
  const { user } = useAuth()
  const [members, setMembers] = useState<Member[]>([])
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<ViewMode>('members')

  // Search and Filter
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountTypeFilter>('all')
  const [showFilters, setShowFilters] = useState(false)

  // Invite Modal
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteFirstName, setInviteFirstName] = useState('')
  const [inviteLastName, setInviteLastName] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [inviteError, setInviteError] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)

  // Create User Modal
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    temp_password: '',
    send_email: true,
    role: 'member' as 'admin' | 'member',
  })
  const [createUserError, setCreateUserError] = useState('')
  const [createUserLoading, setCreateUserLoading] = useState(false)

  // Confirm Modal for removal
  const [confirmRemove, setConfirmRemove] = useState<Member | null>(null)
  const [removeLoading, setRemoveLoading] = useState(false)

  // Confirm Modal for toggle enabled
  const [confirmToggle, setConfirmToggle] = useState<OrgUser | null>(null)
  const [toggleLoading, setToggleLoading] = useState(false)

  // Detail-Drawer
  const [drawerMember, setDrawerMember] = useState<Member | null>(null)

  const isAdminOrOwner = organization.member_role === 'owner' || organization.member_role === 'admin'
  // Kunden-Instanz (TQ #409): cockpit-api kann Mitglieder nur lesen und die Rolle aendern.
  // Einladen/Anlegen/Entfernen/Deaktivieren/Passwort-Reset gibt es dort (noch) nicht -> ausgeblendet.
  const tenant = isTenantInstance()
  const canManage = isAdminOrOwner && !tenant
  // Keycloak-UUID ist der Member-Ref (in der Kunden-Instanz ist `id` immer 0).
  const memberRef = (m: Member): string | number => m.keycloak_id ?? m.id

  useEffect(() => {
    loadMembers()
  }, [organization.slug])

  useEffect(() => {
    if (viewMode === 'users' && isAdminOrOwner) {
      loadOrgUsers()
    }
  }, [viewMode, organization.slug])

  const loadMembers = async () => {
    try {
      const response = await api.getMembers((organization.public_token || organization.slug))
      setMembers(response.members || [])
    } catch (error) {
      console.error('Failed to load members:', error)
      showToast('error', 'Mitglieder konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  const loadOrgUsers = async () => {
    try {
      const response = await api.getOrgUsers((organization.public_token || organization.slug))
      setOrgUsers(response.users || [])
    } catch (error) {
      console.error('Failed to load org users:', error)
      // Silently fail - users view is optional
    }
  }

  // Filtered members based on search and filters
  const filteredMembers = useMemo(() => {
    return members.filter(member => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch = !searchQuery ||
        member.email.toLowerCase().includes(searchLower) ||
        (member.first_name?.toLowerCase().includes(searchLower)) ||
        (member.last_name?.toLowerCase().includes(searchLower)) ||
        getMemberName(member).toLowerCase().includes(searchLower)

      const matchesRole = roleFilter === 'all' || member.role === roleFilter
      const matchesStatus = statusFilter === 'all' || member.status === statusFilter
      const matchesType = accountTypeFilter === 'all' || (member.account_type ?? 'person') === accountTypeFilter

      return matchesSearch && matchesRole && matchesStatus && matchesType
    })
  }, [members, searchQuery, roleFilter, statusFilter, accountTypeFilter])

  // Counts pro Account-Type fuer die Filter-Chips (immer aus dem unfiltered set)
  const typeCounts = useMemo(() => {
    const counts: Record<AccountTypeFilter, number> = { all: members.length, person: 0, service: 0, mailbox: 0 }
    for (const m of members) {
      const t = (m.account_type ?? 'person') as AccountType
      counts[t] += 1
    }
    return counts
  }, [members])

  // Filtered org users
  const filteredOrgUsers = useMemo(() => {
    if (!searchQuery) return orgUsers
    const searchLower = searchQuery.toLowerCase()
    return orgUsers.filter(u =>
      u.email.toLowerCase().includes(searchLower) ||
      (u.first_name?.toLowerCase().includes(searchLower)) ||
      (u.last_name?.toLowerCase().includes(searchLower))
    )
  }, [orgUsers, searchQuery])

  const handleInvite = async () => {
    if (!inviteEmail) return

    setInviteError('')
    setInviteLoading(true)

    try {
      await api.inviteMember((organization.public_token || organization.slug), inviteEmail, inviteRole, inviteFirstName || undefined, inviteLastName || undefined)
      setShowInviteModal(false)
      setInviteEmail('')
      setInviteFirstName('')
      setInviteLastName('')
      setInviteRole('member')
      loadMembers()
      showToast('success', `Einladung an ${inviteEmail} gesendet`)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } }
      setInviteError(err.response?.data?.error || 'Einladung fehlgeschlagen')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleCreateUser = async () => {
    setCreateUserError('')
    setCreateUserLoading(true)

    try {
      const orgRef = organization.public_token || organization.slug
      let invitationSent = true
      if (tenant) {
        const res = await api.createTenantUser(orgRef, {
          email: createUserForm.email,
          firstName: createUserForm.first_name,
          lastName: createUserForm.last_name,
          role: createUserForm.role === 'admin' ? 'admin' : (createUserForm.role as string) === 'operator' ? 'operator' : 'viewer',
        })
        // Nur member.invitation_sent === true gilt als verschickt (cockpit-api liefert es im member-Objekt).
        invitationSent = isInvitationSent(res)
      } else {
        await api.createOrgUser(orgRef, createUserForm)
      }
      setShowCreateUserModal(false)
      setCreateUserForm({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        temp_password: '',
        send_email: true,
        role: 'member',
      })
      loadMembers()
      loadOrgUsers()
      if (!tenant) showToast('success', 'Benutzer erfolgreich erstellt')
      else if (invitationSent) showToast('success', `Einladung an ${createUserForm.email} verschickt`)
      else showToast('error', 'Benutzer angelegt, die Einladung konnte aber nicht verschickt werden')
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { error?: string } } }
      setCreateUserError(createUserErrorMessage(err.response?.status, err.response?.data?.error, tenant))
    } finally {
      setCreateUserLoading(false)
    }
  }

  const handleRemoveConfirm = async () => {
    if (!confirmRemove) return

    setRemoveLoading(true)
    try {
      await api.removeMember((organization.public_token || organization.slug), memberRef(confirmRemove))
      setConfirmRemove(null)
      loadMembers()
      showToast('success', `${getMemberName(confirmRemove)} wurde entfernt`)
    } catch (error) {
      console.error('Failed to remove member:', error)
      showToast('error', 'Mitglied konnte nicht entfernt werden')
    } finally {
      setRemoveLoading(false)
    }
  }

  const handleToggleEnabled = async () => {
    if (!confirmToggle) return

    setToggleLoading(true)
    try {
      const newEnabled = !confirmToggle.enabled
      await api.toggleUserEnabled((organization.public_token || organization.slug), confirmToggle.keycloak_id, newEnabled)
      setConfirmToggle(null)
      loadOrgUsers()
      showToast('success', newEnabled ? 'Benutzer aktiviert' : 'Benutzer deaktiviert')
    } catch (error) {
      console.error('Failed to toggle user:', error)
      showToast('error', 'Status konnte nicht geaendert werden')
    } finally {
      setToggleLoading(false)
    }
  }

  const handleResetPassword = async (user: OrgUser) => {
    try {
      await api.resetUserPassword((organization.public_token || organization.slug), user.keycloak_id)
      showToast('success', `Passwort-Zuruecksetzung an ${displayIdentity(user)} gesendet`)
    } catch (error) {
      console.error('Failed to reset password:', error)
      showToast('error', 'E-Mail konnte nicht gesendet werden')
    }
  }

  const handleRoleChange = async (memberId: string | number, newRole: 'admin' | 'member' | 'operator' | 'viewer', memberName: string) => {
    try {
      await api.updateMemberRole((organization.public_token || organization.slug), memberId, newRole)
      loadMembers()
      showToast('success', `Rolle von ${memberName} geaendert zu ${getRoleLabel(newRole)}`)
    } catch (error) {
      console.error('Failed to update role:', error)
      showToast('error', 'Rolle konnte nicht geaendert werden')
    }
  }

  const getMemberName = (member: Member) => {
    if (member.first_name || member.last_name) {
      return `${member.first_name || ''} ${member.last_name || ''}`.trim()
    }
    return member.email.split('@')[0]
  }

  const getUserName = (user: OrgUser) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim()
    }
    return user.email.split('@')[0]
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Inhaber'
      case 'admin': return 'Administrator'
      case 'member': return 'Mitglied'
      case 'operator': return 'Operator'
      case 'viewer': return 'Betrachter'
      case 'none': return 'Keine Rolle'
      default: return role
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Aktiv'
      case 'pending': return 'Ausstehend'
      case 'removed': return 'Entfernt'
      default: return status
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setRoleFilter('all')
    setStatusFilter('all')
  }

  const hasActiveFilters = searchQuery || roleFilter !== 'all' || statusFilter !== 'all'

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Mitglieder</h1>
            <p className="page-subtitle">Wird geladen...</p>
          </div>
        </div>
        <SkeletonTable rows={5} />
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Mitglieder</h1>
          <p className="page-subtitle">
            {viewMode === 'members'
              ? (filteredMembers.length === members.length
                  ? `${members.length} Mitglieder in dieser Organisation`
                  : `${filteredMembers.length} von ${members.length} Mitgliedern`)
              : `${filteredOrgUsers.length} Benutzer`
            }
          </p>
        </div>
        {isAdminOrOwner && (
          <div className="header-actions">
            <button
              className="btn-secondary"
              onClick={() => setShowCreateUserModal(true)}
              title="Direkt einen neuen Login-Account anlegen — du vergibst Benutzername und (optional) Initialpasswort. Fuer Personen ohne eigene E-Mail oder fuer Service-Accounts."
            >
              <PlusIcon size={18} />
              Benutzer anlegen
            </button>
            {canManage && <button
              className="btn-primary"
              onClick={() => setShowInviteModal(true)}
              title="Per E-Mail einladen — der/die Eingeladene legt eigenes Passwort fest. Standardweg fuer echte Personen mit eigener E-Mail-Adresse."
            >
              <PlusIcon size={18} />
              Mitglied einladen
            </button>}
          </div>
        )}
      </div>

      {/* View Mode Tabs */}
      {isAdminOrOwner && (
        <div className="view-tabs">
          <button
            className={`view-tab ${viewMode === 'members' ? 'active' : ''}`}
            onClick={() => setViewMode('members')}
          >
            Mitglieder
          </button>
          <button
            className={`view-tab ${viewMode === 'users' ? 'active' : ''}`}
            onClick={() => setViewMode('users')}
          >
            Benutzerverwaltung
          </button>
        </div>
      )}

      {/* Identitaets-Typ Filter Chips — nur fuer 'members' View.
          Postfach-Typ existiert im Modell, wird aber NICHT als Chip
          angezeigt: Shared-Mailboxes/Aliase gehoeren in den
          Postfaecher-Tab unter Mail, nicht in die Mitglieder-Liste. */}
      {viewMode === 'members' && (
        <div style={{ display: 'flex', gap: 8, margin: '12px 0', flexWrap: 'wrap', alignItems: 'center' }}>
          {(['all', 'person', 'service'] as const).map((t) => {
            const active = accountTypeFilter === t
            const label = t === 'all' ? 'Alle' : ACCOUNT_TYPE_LABEL[t as AccountType]
            const icon = t === 'all' ? '⊕' : ACCOUNT_TYPE_ICON[t as AccountType]
            const tip = t === 'all'
              ? 'Alle Mitglieder anzeigen'
              : t === 'person'
                ? 'Echte Personen mit Login (Inhaber, Admins, Mitglieder)'
                : 'Service-Accounts (z.B. Aria-Assistent, Funktions-Accounts)'
            return (
              <button
                key={t}
                type="button"
                onClick={() => setAccountTypeFilter(t)}
                title={tip}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  border: active ? '1px solid #3b82f6' : '1px solid #d1d5db',
                  background: active ? '#3b82f6' : '#fff',
                  color: active ? '#fff' : '#374151',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span aria-hidden>{icon}</span>
                <span>{label}</span>
                <span style={{
                  background: active ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                  padding: '1px 7px',
                  borderRadius: 999,
                  fontSize: '0.75rem',
                }}>{typeCounts[t]}</span>
              </button>
            )
          })}
          <span style={{
            color: '#6b7280',
            fontSize: '0.75rem',
            marginLeft: 4,
          }}>
            Postfaecher findest du im Tab <em>Services &rarr; Mail &rarr; Postfaecher</em>.
          </span>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="search-filter-bar">
        <div className="search-wrapper">
          <SearchIcon size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Nach Name oder E-Mail suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        {viewMode === 'members' && (
          <button
            className={`btn-filter ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterIcon size={18} />
            Filter
            {hasActiveFilters && <span className="filter-badge">{[roleFilter !== 'all', statusFilter !== 'all'].filter(Boolean).length || ''}</span>}
          </button>
        )}
      </div>

      {/* Filter Options */}
      {showFilters && viewMode === 'members' && (
        <div className="filter-options">
          <div className="filter-group">
            <label>Rolle</label>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}>
              <option value="all">Alle Rollen</option>
              <option value="owner">Inhaber</option>
              <option value="admin">Administrator</option>
              {tenant ? (
                <>
                  <option value="operator">Operator</option>
                  <option value="viewer">Betrachter</option>
                </>
              ) : (
                <option value="member">Mitglied</option>
              )}
            </select>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="pending">Ausstehend</option>
              <option value="removed">Entfernt</option>
            </select>
          </div>
          {hasActiveFilters && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Filter zuruecksetzen
            </button>
          )}
        </div>
      )}

      {/* Members List View */}
      {viewMode === 'members' && (
        <>
          {filteredMembers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <SearchIcon size={48} color="#ccc" />
              </div>
              <h3>Keine Mitglieder gefunden</h3>
              <p>
                {hasActiveFilters
                  ? 'Versuchen Sie andere Suchbegriffe oder Filter.'
                  : 'Laden Sie Mitglieder ein, um loszulegen.'
                }
              </p>
              {hasActiveFilters && (
                <button className="btn-secondary" onClick={clearFilters}>
                  Filter zuruecksetzen
                </button>
              )}
            </div>
          ) : (
            <div className="members-list">
              {filteredMembers.map((member) => {
                const acctType = (member.account_type ?? 'person') as AccountType
                const colors = ACCOUNT_TYPE_COLORS[acctType]
                return (
                <div
                  key={member.keycloak_id || `m-${member.id}`}
                  className="member-card"
                  onClick={() => setDrawerMember(member)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="member-avatar" title={ACCOUNT_TYPE_LABEL[acctType]}>
                    {acctType === 'person'
                      ? getMemberName(member).charAt(0).toUpperCase()
                      : ACCOUNT_TYPE_ICON[acctType]}
                  </div>
                  <div className="member-info">
                    <span className="member-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {getMemberName(member)}
                      <span style={{
                        background: colors.bg,
                        color: colors.fg,
                        padding: '1px 8px',
                        borderRadius: 4,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                      }}>
                        {ACCOUNT_TYPE_LABEL[acctType]}
                      </span>
                    </span>
                    <span className="member-email">{displayIdentity(member)}</span>
                  </div>
                  <div className="member-role">
                    {isAdminOrOwner && member.role !== 'owner' && member.keycloak_id !== user?.sub ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleRoleChange(memberRef(member), e.target.value as 'admin' | 'member' | 'operator' | 'viewer', getMemberName(member))}
                        onClick={(e) => e.stopPropagation()}
                        className="role-select"
                      >
                        {member.role === 'none' && <option value="none" disabled>Keine Rolle</option>}
                        <option value="admin">Administrator</option>
                        {tenant ? (
                          <>
                            <option value="operator">Operator</option>
                            <option value="viewer">Betrachter</option>
                          </>
                        ) : (
                          <option value="member">Mitglied</option>
                        )}
                      </select>
                    ) : (
                      <span
                        className={`role-badge role-${member.role}`}
                        title={
                          member.keycloak_id === user?.sub
                            ? 'Eigene Rolle kann hier nicht geändert werden (Self-Protection)'
                            : member.role === 'owner'
                              ? 'Owner-Rolle kann nicht geändert werden'
                              : undefined
                        }
                      >
                        {getRoleLabel(member.role)}
                      </span>
                    )}
                  </div>
                  <div className="member-status">
                    <span className={`status-badge status-${member.status}`}>
                      {getStatusLabel(member.status)}
                    </span>
                  </div>
                  {canManage && member.role !== 'owner' && member.keycloak_id !== user?.sub && (
                    <button
                      className="btn-remove"
                      onClick={(e) => { e.stopPropagation(); setConfirmRemove(member) }}
                      title="Mitglied entfernen"
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Users Management View */}
      {viewMode === 'users' && isAdminOrOwner && (
        <div className="members-list">
          {filteredOrgUsers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <SearchIcon size={48} color="#ccc" />
              </div>
              <h3>Keine Benutzer gefunden</h3>
              <p>Erstellen Sie Benutzer, um loszulegen.</p>
            </div>
          ) : (
            filteredOrgUsers.map((user) => (
              <div key={user.keycloak_id} className="member-card">
                <div className="member-avatar">
                  {getUserName(user).charAt(0).toUpperCase()}
                </div>
                <div className="member-info">
                  <span className="member-name">{getUserName(user)}</span>
                  <span className="member-email">{displayIdentity(user)}</span>
                </div>
                <div className="member-role">
                  <span className={`role-badge role-${user.role}`}>
                    {getRoleLabel(user.role)}
                  </span>
                </div>
                <div className="member-status">
                  {user.enabled !== null && (
                    <span className={`status-badge ${user.enabled ? 'status-active' : 'status-disabled'}`}>
                      {user.enabled ? 'Aktiv' : 'Deaktiviert'}
                    </span>
                  )}
                </div>
                <div className="user-actions">
                  {canManage && user.enabled !== null && user.role !== 'owner' && (
                    <button
                      className={`btn-action ${user.enabled ? 'btn-action-warning' : 'btn-action-success'}`}
                      onClick={() => setConfirmToggle(user)}
                      title={user.enabled ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {user.enabled ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                  )}
                  {canManage && <button
                    className="btn-action"
                    onClick={() => handleResetPassword(user)}
                    title="Passwort zuruecksetzen"
                  >
                    PW Reset
                  </button>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mitglied einladen</h2>
              <p className="modal-subtitle">Senden Sie eine Einladung an eine E-Mail-Adresse</p>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>E-Mail-Adresse *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="email@beispiel.de"
                  autoFocus
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Vorname</label>
                  <input
                    type="text"
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                    placeholder="Max"
                  />
                </div>
                <div className="form-group">
                  <label>Nachname</label>
                  <input
                    type="text"
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                    placeholder="Mustermann"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Rolle</label>
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}>
                  <option value="member">Mitglied</option>
                  <option value="admin">Administrator</option>
                </select>
                <span className="form-hint">
                  {inviteRole === 'admin'
                    ? 'Administratoren koennen Mitglieder verwalten und Einstellungen aendern.'
                    : 'Mitglieder koennen Services nutzen und ihre Profile verwalten.'
                  }
                </span>
              </div>
              {inviteError && <div className="error-message">{inviteError}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowInviteModal(false)}>
                Abbrechen
              </button>
              <button className="btn-primary" onClick={handleInvite} disabled={inviteLoading || !inviteEmail}>
                {inviteLoading ? 'Wird gesendet...' : 'Einladung senden'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="modal-overlay" onClick={() => setShowCreateUserModal(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Benutzer anlegen</h2>
              <p className="modal-subtitle">{tenant ? 'Der Benutzer erhält eine E-Mail mit einem Link, um das Passwort selbst festzulegen.' : 'Erstellen Sie einen neuen Benutzer direkt im System'}</p>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Vorname *</label>
                  <input
                    type="text"
                    value={createUserForm.first_name}
                    onChange={(e) => setCreateUserForm({...createUserForm, first_name: e.target.value})}
                    placeholder="Max"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Nachname *</label>
                  <input
                    type="text"
                    value={createUserForm.last_name}
                    onChange={(e) => setCreateUserForm({...createUserForm, last_name: e.target.value})}
                    placeholder="Mustermann"
                  />
                </div>
              </div>
              <div className="form-row">
                {!tenant && <div className="form-group">
                  <label>Benutzername *</label>
                  <input
                    type="text"
                    value={createUserForm.username}
                    onChange={(e) => setCreateUserForm({...createUserForm, username: e.target.value})}
                    placeholder="max.mustermann"
                  />
                </div>}
                <div className="form-group">
                  <label>E-Mail *</label>
                  <input
                    type="email"
                    value={createUserForm.email}
                    onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                    placeholder="max@beispiel.de"
                  />
                </div>
              </div>
              <div className="form-row">
                {!tenant && <div className="form-group">
                  <label>Temporaeres Passwort</label>
                  <input
                    type="text"
                    value={createUserForm.temp_password}
                    onChange={(e) => setCreateUserForm({...createUserForm, temp_password: e.target.value})}
                    placeholder="Leer = E-Mail mit Link"
                  />
                  <span className="form-hint">
                    Wenn leer, wird eine E-Mail zur Passwort-Erstellung gesendet.
                  </span>
                </div>}
                <div className="form-group">
                  <label>Rolle *</label>
                  <select
                    value={createUserForm.role}
                    onChange={(e) => setCreateUserForm({...createUserForm, role: e.target.value as 'admin' | 'member'})}
                  >
                    {tenant ? (
                      <>
                        <option value="member">Betrachter</option>
                        <option value="operator">Operator</option>
                        <option value="admin">Administrator</option>
                      </>
                    ) : (
                      <>
                        <option value="member">Mitglied</option>
                        <option value="admin">Administrator</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
              {!tenant && <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={createUserForm.send_email}
                    onChange={(e) => setCreateUserForm({...createUserForm, send_email: e.target.checked})}
                  />
                  Willkommens-E-Mail senden
                </label>
              </div>}
              {createUserError && <div className="error-message">{createUserError}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateUserModal(false)}>
                Abbrechen
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateUser}
                disabled={createUserLoading || (!tenant && !createUserForm.username) || !createUserForm.email || !createUserForm.first_name || !createUserForm.last_name}
              >
                {createUserLoading ? 'Wird erstellt...' : 'Benutzer anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Remove Modal */}
      <ConfirmModal
        isOpen={!!confirmRemove}
        title="Mitglied entfernen"
        message={
          <>
            Moechten Sie <strong>{confirmRemove ? getMemberName(confirmRemove) : ''}</strong> wirklich aus der Organisation entfernen?
            <br /><br />
            Diese Person verliert Zugriff auf alle Services der Organisation.
          </>
        }
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        variant="danger"
        isLoading={removeLoading}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setConfirmRemove(null)}
      />

      {/* Confirm Toggle Enabled Modal */}
      <ConfirmModal
        isOpen={!!confirmToggle}
        title={confirmToggle?.enabled ? 'Benutzer deaktivieren' : 'Benutzer aktivieren'}
        message={
          <>
            Moechten Sie <strong>{confirmToggle ? getUserName(confirmToggle) : ''}</strong> wirklich {confirmToggle?.enabled ? 'deaktivieren' : 'aktivieren'}?
            <br /><br />
            {confirmToggle?.enabled
              ? 'Der Benutzer kann sich nicht mehr anmelden, bis er wieder aktiviert wird.'
              : 'Der Benutzer kann sich wieder anmelden.'
            }
          </>
        }
        confirmLabel={confirmToggle?.enabled ? 'Deaktivieren' : 'Aktivieren'}
        cancelLabel="Abbrechen"
        variant={confirmToggle?.enabled ? 'danger' : 'info'}
        isLoading={toggleLoading}
        onConfirm={handleToggleEnabled}
        onCancel={() => setConfirmToggle(null)}
      />

      {drawerMember && (
        <MemberDetailDrawer
          orgSlug={organization.slug}
          memberRef={drawerMember.keycloak_id || drawerMember.id}
          isAdminOrOwner={isAdminOrOwner}
          onClose={() => setDrawerMember(null)}
          onChanged={() => { void loadMembers() }}
        />
      )}
    </div>
  )
}
